import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';

/**
 * Automated booking agreement — a standing instruction to grab the same slot
 * every week as soon as it opens.
 *
 * Read-only, like the old page. What it is read for is whether it is *working*,
 * so the counts and the last result come first, as a summary, instead of being
 * six rows in a list of twenty-five. A failing agreement is the whole reason
 * anyone opens this.
 *
 * The attempt table is the old page's, with the ids linking where they can:
 * activities and signups both have rebuilt pages now.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const BookingAgreementInternal = ({ api }) => {
  const { agreementId } = useParams();

  const [agreement, setAgreement] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.automatedBookingAgreements.getAgreementById(agreementId);
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setAgreement(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, agreementId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/booking-agreements">← All booking agreements</BackToList>
        <div className="next-note next-note--error">
          Could not load this agreement.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!agreement) return <div className="next-page next-muted">Loading…</div>;

  const attempts = Array.isArray(agreement.attempts) ? agreement.attempts : [];

  const facts = [
    ['User', [agreement.userName, agreement.userEmail].filter(Boolean).join(' — ') || '—'],
    ['User id', agreement.userId
      ? <Guid value={agreement.userId} to={`/next/user/${agreement.userId}`} />
      : <span className="next-muted">—</span>],
    ['Facility', agreement.facilityName || <span className="next-muted">—</span>],
    ['Facility id', agreement.facilityId
      ? <Guid value={agreement.facilityId} to={`/next/facility/${agreement.facilityId}`} />
      : <span className="next-muted">—</span>],
    ['Product', agreement.productName || <span className="next-muted">—</span>],
    ['Product id', agreement.productId
      ? <Guid value={agreement.productId} href={`/product/details/${agreement.productId}`} />
      : <span className="next-muted">—</span>],
    ['Day of week', agreement.dayOfWeek || <span className="next-muted">—</span>],
    ['Time of day', agreement.timeOfDay || <span className="next-muted">—</span>],
    ['Duration', agreement.durationMinutes ? `${agreement.durationMinutes} min` : <span className="next-muted">—</span>],
    ['Preferred court', agreement.preferredCourtName || <span className="next-muted">any</span>],
    ['Active from', fmtDate(agreement.startDateCet)],
    ['Active to', fmtDate(agreement.endDateCet)],
    ['Created at (CET)', fmtDate(agreement.createdAtCet)],
    ['Updated at (CET)', fmtDate(agreement.updatedAtCet)]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/booking-agreements">← All booking agreements</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>
            {[agreement.dayOfWeek, agreement.timeOfDay].filter(Boolean).join(' ') || 'Booking agreement'}
          </h1>
          {agreement.status && <StatusPill status={agreement.status}>{agreement.status}</StatusPill>}
        </div>
        <span className="next-detail__id">{agreement.id}</span>
      </header>

      <div className="next-detail">
        <div className="next-detail__main">
          {/* Is it working, and when does it try next. */}
          <section className="next-card next-summary">
            <div className="next-summary__cell">
              <span className="next-summary__label">Attempts</span>
              <span className="next-summary__big">{agreement.attemptCount ?? 0}</span>
              <span className="next-summary__sub">
                {agreement.successfulAttemptCount ?? 0} succeeded · {agreement.failedAttemptCount ?? 0} failed
              </span>
            </div>
            <div className="next-summary__cell">
              <span className="next-summary__label">Last attempt</span>
              <span className="next-summary__name">
                {agreement.lastAttemptResult || '—'}
              </span>
              <span className="next-summary__sub">{fmtDate(agreement.lastAttemptStartedAtCet)}</span>
              <span className="next-summary__sub">
                {agreement.lastSuccessfulAttemptCet
                  ? `Last success ${fmtDate(agreement.lastSuccessfulAttemptCet)}`
                  : 'Never succeeded'}
              </span>
            </div>
            <div className="next-summary__cell">
              <span className="next-summary__label">Next scheduled</span>
              <span className="next-summary__name">
                {agreement.upcomingScheduledAttemptCet
                  ? fmtDate(agreement.upcomingScheduledAttemptCet)
                  : 'None scheduled'}
              </span>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Agreement</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Attempts</h2>
              <span className="next-muted">{attempts.length} recorded</span>
            </div>

            {attempts.length === 0 ? (
              <p className="next-table__note next-muted">No attempt recorded yet.</p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Attempt</th>
                      <th>Result</th>
                      <th>Slot</th>
                      <th>Court</th>
                      <th>Signup</th>
                      <th>Created (CET)</th>
                      <th>Bookable from</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.map(attempt => (
                      <tr key={attempt.id}>
                        <td data-label=""><Guid value={attempt.id} /></td>
                        <td data-label="Result">
                          {attempt.result
                            ? <StatusPill status={attempt.result}>{attempt.result}</StatusPill>
                            : <span className="next-muted">—</span>}
                        </td>
                        <td data-label="Slot" className="next-nowrap">
                          {attempt.slotStartCet
                            ? `${fmtDate(attempt.slotStartCet)}${attempt.slotEndCet ? ` → ${String(attempt.slotEndCet).slice(11, 16)}` : ''}`
                            : '—'}
                        </td>
                        <td data-label="Court">{attempt.chosenCourtName || <span className="next-muted">—</span>}</td>
                        <td data-label="Signup">
                          {attempt.signupId
                            ? <Guid value={attempt.signupId} to={`/next/signup/${attempt.signupId}`} />
                            : (attempt.activityId
                              ? <Guid value={attempt.activityId} to={`/next/activity/${attempt.activityId}`} />
                              : <span className="next-muted">—</span>)}
                        </td>
                        <td data-label="Created (CET)" className="next-nowrap next-muted">{fmtDate(attempt.createdAtCet)}</td>
                        <td data-label="Bookable from" className="next-nowrap">{fmtDate(attempt.bookableFromCet)}</td>
                        <td data-label="Message">{attempt.message || <span className="next-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                agreement.userId && { label: 'User', hint: agreement.userEmail, to: `/next/user/${agreement.userId}` },
                agreement.userId && {
                  label: 'Their agreements',
                  to: `/next/booking-agreements?user=${agreement.userId}`
                },
                agreement.facilityId && { label: 'Facility', to: `/next/facility/${agreement.facilityId}` },
                agreement.facilityId && {
                  label: 'Agreements here',
                  hint: 'same facility',
                  to: `/next/booking-agreements?facilityId=${agreement.facilityId}`
                },
                agreement.userId && {
                  label: 'Their signups',
                  to: `/next/signups?user=${agreement.userId}`
                },
                agreement.productId && { label: 'Product', href: `/product/details/${agreement.productId}` }
              ]}
            />
          </section>

          {/* No Actions panel: the old page has none. An agreement is created and
              stopped by the user in the app. */}
        </aside>
      </div>
    </div>
  );
};

BookingAgreementInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const BookingAgreement = withApi(BookingAgreementInternal);

BookingAgreement.requiresLogin = true;

export default BookingAgreement;
